// Port of ios/Sources/ViewModels/ReactionActivity.swift.
//
// The Actividad tab's "Reacciones" rows (2026-10-09, Slack's Activity): who reacted, with what,
// to which of the reader's own messages, and where. Twin of Android's `ReactionActivity` — newest
// first, one row per (person, emoji, message), a reaction whose message is gone is dropped, and
// the emoji is the character when this client knows the name, else `:name:`.
import type { PostRecord, ReactionRecord, UserRecord } from './records';
import { messagePreview, userDisplayName } from './channelListDerivation';
import { EmojiCharacters } from './emojiCharacters';
import { RelativeTime } from './relativeTime';

export interface ReactionActivityRow {
  /** `postId:userId:emojiName`. */
  id: string;
  postId: string;
  channelId: string;
  channelName: string;
  actorName: string;
  emoji: string;
  preview: string;
  timeLabel: string;
  createAt: number;
}

/**
 * Swift calls `RelativeTime.format(epochMillis:now:labels: .init(yesterday:))` directly, and so
 * does the default here (in `timeZone`); the seam exists for tests and for a caller that formats
 * in another zone or locale.
 */
export type RelativeTimeFormat = (epochMillis: number, now: number, labels: { yesterday: string }) => string;

export interface ReactionActivityArgs {
  reactions: readonly ReactionRecord[];
  postsById: ReadonlyMap<string, PostRecord>;
  usersById: ReadonlyMap<string, UserRecord>;
  channelNames: ReadonlyMap<string, string>;
  now: number;
  yesterdayLabel: string;
  attachmentPlaceholder: string;
  unknownActorLabel: string;
  unknownChannelLabel: string;
  /** IANA zone for the default time label; undefined = the machine's. */
  timeZone?: string;
  relativeTime?: RelativeTimeFormat;
}

function rows(args: ReactionActivityArgs): ReactionActivityRow[] {
  const relativeTime: RelativeTimeFormat =
    args.relativeTime ?? ((ms, now, labels) => RelativeTime.format(ms, now, labels, args.timeZone));
  const seen = new Set<string>();
  const out: ReactionActivityRow[] = [];
  // Newest first; a stable sort keeps arrival order among equal times, as Swift's does.
  const sorted = [...args.reactions].sort((a, b) => b.createAt - a.createAt);
  for (const reaction of sorted) {
    const key = reaction.postId + ':' + reaction.userId + ':' + reaction.emojiName;
    if (seen.has(key)) continue;
    seen.add(key);
    const post = args.postsById.get(reaction.postId);
    if (!post) continue;
    const actor = args.usersById.get(reaction.userId);
    out.push({
      id: key,
      postId: post.id,
      channelId: post.channelId,
      channelName: args.channelNames.get(post.channelId) ?? args.unknownChannelLabel,
      actorName: actor ? userDisplayName(actor) : args.unknownActorLabel,
      emoji: EmojiCharacters.character(reaction.emojiName) ?? `:${reaction.emojiName}:`,
      preview: messagePreview({
        message: post.message,
        authorName: null,
        isOwnMessage: false,
        showsAuthorPrefix: false,
        youLabel: '',
        attachmentPlaceholder: args.attachmentPlaceholder,
      }),
      timeLabel: relativeTime(reaction.createAt, args.now, { yesterday: args.yesterdayLabel }),
      createAt: reaction.createAt,
    });
  }
  return out;
}

export const ReactionActivity = {
  rows,
} as const;
